import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import SendReminderButton from "../components/SendReminderButton";
import {
  Dumbbell,
  CalendarDays,
  MapPin,
  FileText,
  Users,
  ChevronLeft,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Minus,
  UserPlus,
  Plus,
  X,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type Training = {
  id: string;
  date: string;
  team_id: string | null;
  notes: string | null;
  location: string | null;
};

type AttendanceRecord = {
  id: string;
  was_present: boolean;
};

type JsonPlayer = {
  id: string;
  firstName?: string;
  lastName?: string;
  number?: string | number;
};

type ClubTeamRow = {
  id: string;
  team_name: string;
  players: JsonPlayer[] | null;
};

type PlayerEntry = {
  jsonId: string;
  name: string;
  guestTeamName?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatFullDate(iso: string) {
  return new Date(iso).toLocaleString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ClubTrainingDetails() {
  const { clubId, trainingId } = useParams<{ clubId: string; trainingId: string }>();

  const [training, setTraining] = useState<Training | null>(null);
  const [teamName, setTeamName] = useState("");
  // All players on the team (for coaches view), plus guest players added from other teams
  const [allPlayers, setAllPlayers] = useState<PlayerEntry[]>([]);
  // Attendance keyed by player_json_id
  const [attendanceMap, setAttendanceMap] = useState<Map<string, AttendanceRecord>>(new Map());
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState<string | null>(null);

  // Other teams of the club (to add a guest player to this training's attendance)
  const [clubTeams, setClubTeams] = useState<ClubTeamRow[]>([]);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestTeamId, setGuestTeamId] = useState("");
  const [addingGuestId, setAddingGuestId] = useState<string | null>(null);

  useEffect(() => {
    if (!trainingId || !clubId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Utilisateur non connecté.");

        // 1. Training + attendance + club role + club teams (for guest players) in parallel
        const [trainingRes, attendanceRes, clubRoleRes, clubTeamsRes] = await Promise.all([
          supabase
            .from("trainings")
            .select("id, date, team_id, notes, location")
            .eq("id", trainingId)
            .maybeSingle(),
          supabase
            .from("training_attendance")
            .select("id, player_json_id, player_id, was_present")
            .eq("training_id", trainingId),
          supabase
            .from("club_members")
            .select("role")
            .eq("club_id", clubId)
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("teams")
            .select("id, team_name, players")
            .eq("club_id", clubId),
        ]);

        if (trainingRes.error) throw trainingRes.error;
        if (attendanceRes.error) throw attendanceRes.error;

        const t = trainingRes.data as Training | null;
        if (!t) throw new Error("Entraînement introuvable.");

        const clubTeamsData = (clubTeamsRes.data as ClubTeamRow[] | null) || [];

        // Index of players from OTHER teams of the club (jsonId -> name/team), for guest resolution
        const otherTeamPlayerIndex = new Map<string, { name: string; teamName: string }>();
        for (const ct of clubTeamsData) {
          if (ct.id === t.team_id) continue;
          const list: JsonPlayer[] = Array.isArray(ct.players) ? ct.players : [];
          for (const p of list) {
            if (!p.id) continue;
            const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || `Joueur #${p.number ?? p.id}`;
            otherTeamPlayerIndex.set(p.id, { name, teamName: ct.team_name });
          }
        }

        // 2. Team-specific queries (only if team_id is known)
        let teamRoleRes: any = null;
        let teamRes: any = null;
        let membersRes: any = null;

        if (t.team_id) {
          [teamRoleRes, teamRes, membersRes] = await Promise.all([
            supabase
              .from("team_members")
              .select("role")
              .eq("team_id", t.team_id)
              .eq("user_id", user.id)
              .maybeSingle(),
            supabase
              .from("teams")
              .select("team_name, players")
              .eq("id", t.team_id)
              .maybeSingle(),
            supabase
              .from("team_members")
              .select("player_json_id, user_id")
              .eq("team_id", t.team_id)
              .not("player_json_id", "is", null),
          ]);
        }

        const clubRole = clubRoleRes?.data?.role ?? null;
        const teamRole = teamRoleRes?.data?.role ?? null;
        const editable =
          ["dirigeant", "cadre_technique"].includes(clubRole) ||
          ["owner", "coach", "assistant"].includes(teamRole);

        // 3. Build name map from teams.players JSON
        let tName = "Équipe";
        const nameMap = new Map<string, string>();

        if (teamRes?.data) {
          if (teamRes.data.team_name) tName = teamRes.data.team_name.trim();

          const jsonPlayers: JsonPlayer[] = Array.isArray(teamRes.data.players)
            ? (teamRes.data.players as JsonPlayer[])
            : [];

          for (const p of jsonPlayers) {
            if (!p.id) continue;
            const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
            nameMap.set(p.id, name || `Joueur #${p.number ?? p.id}`);
          }
        }

        // 4. Overwrite with user_profiles where linked
        if (membersRes?.data && membersRes.data.length > 0) {
          const userIds = (membersRes.data as any[]).map((m) => m.user_id).filter(Boolean);
          if (userIds.length > 0) {
            const profilesRes = await supabase
              .from("user_profiles")
              .select("id, full_name, pseudo, public_use_pseudo")
              .in("id", userIds);

            const profileMap = new Map<string, string>();
            for (const prof of profilesRes.data || []) {
              const name =
                prof.public_use_pseudo && prof.pseudo
                  ? prof.pseudo
                  : prof.full_name?.trim() || null;
              if (name) profileMap.set(prof.id, name);
            }

            for (const member of membersRes.data as any[]) {
              if (!member.player_json_id || !member.user_id) continue;
              const profName = profileMap.get(member.user_id);
              if (profName) nameMap.set(member.player_json_id, profName);
            }
          }
        }

        // 5. Build attendance map
        const aMap = new Map<string, AttendanceRecord>();
        for (const row of (attendanceRes.data || []) as any[]) {
          const key = row.player_json_id ?? row.player_id;
          if (!key) continue;
          aMap.set(key, { id: row.id, was_present: row.was_present });
        }

        // 6. Build full player list (all team players, sorted by name)
        const players: PlayerEntry[] = Array.from(nameMap.entries())
          .map(([jsonId, name]) => ({ jsonId, name }));

        // 7. Reconstruct already-added guest players (recorded in attendance but not on this team)
        for (const [jsonId] of aMap.entries()) {
          if (nameMap.has(jsonId)) continue;
          const guest = otherTeamPlayerIndex.get(jsonId);
          if (guest) players.push({ jsonId, name: guest.name, guestTeamName: guest.teamName });
        }

        players.sort((a, b) => a.name.localeCompare(b.name, "fr"));

        if (!cancelled) {
          setTraining(t);
          setTeamName(tName);
          setAllPlayers(players);
          setAttendanceMap(aMap);
          setCanEdit(editable);
          setClubTeams(clubTeamsData);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger l'entraînement.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [trainingId, clubId]);

  // ── Toggle attendance ──────────────────────────────────────────────────────

  async function handleToggle(jsonId: string, newPresent: boolean) {
    if (!trainingId || saving) return;
    setSaving(jsonId);

    const existing = attendanceMap.get(jsonId);

    if (existing) {
      const { error } = await supabase
        .from("training_attendance")
        .update({ was_present: newPresent })
        .eq("id", existing.id);

      if (!error) {
        setAttendanceMap((prev) => {
          const next = new Map(prev);
          next.set(jsonId, { ...existing, was_present: newPresent });
          return next;
        });
      }
    } else {
      const { data, error } = await supabase
        .from("training_attendance")
        .insert({ training_id: trainingId, player_json_id: jsonId, was_present: newPresent })
        .select("id")
        .single();

      if (!error && data) {
        setAttendanceMap((prev) => {
          const next = new Map(prev);
          next.set(jsonId, { id: data.id, was_present: newPresent });
          return next;
        });
      }
    }

    setSaving(null);
  }

  // ── Add a guest player from another team of the club ──────────────────────

  async function handleAddGuest(jsonId: string, name: string, guestTeamName: string) {
    if (!trainingId || addingGuestId) return;
    setAddingGuestId(jsonId);

    const { data, error } = await supabase
      .from("training_attendance")
      .insert({ training_id: trainingId, player_json_id: jsonId, was_present: true })
      .select("id")
      .single();

    if (!error && data) {
      setAttendanceMap((prev) => {
        const next = new Map(prev);
        next.set(jsonId, { id: data.id, was_present: true });
        return next;
      });
      setAllPlayers((prev) =>
        prev.some((p) => p.jsonId === jsonId)
          ? prev
          : [...prev, { jsonId, name, guestTeamName }].sort((a, b) => a.name.localeCompare(b.name, "fr"))
      );
    }

    setAddingGuestId(null);
  }

  // ── Derived counts ─────────────────────────────────────────────────────────

  const counts = useMemo(() => {
    let present = 0;
    let absent = 0;
    attendanceMap.forEach((r) => { r.was_present ? present++ : absent++; });
    return { present, absent, total: attendanceMap.size };
  }, [attendanceMap]);

  const isUpcoming = training ? new Date(training.date) >= new Date() : false;

  const guestTeamOptions = useMemo(
    () => clubTeams.filter((ct) => ct.id !== training?.team_id),
    [clubTeams, training]
  );

  const guestCandidatePlayers = useMemo(() => {
    const ct = guestTeamOptions.find((tm) => tm.id === guestTeamId);
    if (!ct) return [];
    const list: JsonPlayer[] = Array.isArray(ct.players) ? ct.players : [];
    const already = new Set(allPlayers.map((p) => p.jsonId));
    return list
      .filter((p) => p.id && !already.has(p.id))
      .map((p) => ({
        jsonId: p.id,
        name: [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || `Joueur #${p.number ?? p.id}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [guestTeamOptions, guestTeamId, allPlayers]);

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement de l'entraînement…
        </div>
      </div>
    );
  }

  if (error || !training) {
    return (
      <div className="space-y-4">
        <Link
          to={`/club/${clubId}/trainings`}
          className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Retour aux entraînements
        </Link>
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300 inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error || "Entraînement introuvable."}
        </div>
      </div>
    );
  }

  // Players visible to non-editors: only those with a recorded attendance
  const visiblePlayers = canEdit
    ? allPlayers
    : allPlayers.filter((p) => attendanceMap.has(p.jsonId));

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Back */}
      <Link
        to={`/club/${clubId}/trainings`}
        className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 hover:underline"
      >
        <ChevronLeft className="h-4 w-4" />
        Retour aux entraînements
      </Link>

      {/* Header */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Entraînement</div>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Dumbbell className="h-5 w-5 text-gray-500" />
              {teamName}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isUpcoming ? (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                <Clock className="h-3 w-3" />
                À venir
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                Passé
              </span>
            )}
            {canEdit && isUpcoming && trainingId && (
              <SendReminderButton type="training" eventId={trainingId} />
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <CalendarDays className="h-4 w-4" />
              Date &amp; heure
            </div>
            <div className="mt-2 text-sm text-gray-900 dark:text-gray-100 capitalize">
              {formatFullDate(training.date)}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <MapPin className="h-4 w-4" />
              Lieu
            </div>
            <div className="mt-2 text-sm text-gray-900 dark:text-gray-100">
              {training.location?.trim() || <span className="text-gray-400 dark:text-gray-600">—</span>}
            </div>
          </div>

          {training.notes && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4 sm:col-span-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <FileText className="h-4 w-4" />
                Notes
              </div>
              <div className="mt-2 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                {training.notes}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Attendance */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <Users className="h-4 w-4" />
            Présences
            {visiblePlayers.length > 0 && (
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                ({visiblePlayers.length} joueur{visiblePlayers.length !== 1 ? "s" : ""})
              </span>
            )}
          </div>
          {canEdit && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Cliquez sur un joueur pour modifier sa présence
              </span>
              <button
                type="button"
                onClick={() => { setGuestTeamId(guestTeamOptions[0]?.id ?? ""); setGuestModalOpen(true); }}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-dashed border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Ajouter un joueur d'une autre équipe
              </button>
            </div>
          )}
        </div>

        {counts.total > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Présents : {counts.present}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              <XCircle className="h-3.5 w-3.5" />
              Absents : {counts.absent}
            </span>
            {canEdit && allPlayers.length - counts.total > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <Minus className="h-3.5 w-3.5" />
                Non renseignés : {allPlayers.length - counts.total}
              </span>
            )}
          </div>
        )}

        {visiblePlayers.length === 0 ? (
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {canEdit
              ? "Aucun joueur trouvé dans cette équipe."
              : isUpcoming
              ? "Les présences seront enregistrées après l'entraînement."
              : "Aucune présence enregistrée pour cet entraînement."}
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {visiblePlayers.map((p) => {
              const record = attendanceMap.get(p.jsonId);
              const isSaving = saving === p.jsonId;

              return (
                <div
                  key={p.jsonId}
                  className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-2.5"
                >
                  <div className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-2 flex-wrap">
                    {p.name}
                    {p.guestTeamName && (
                      <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                        {p.guestTeamName}
                      </span>
                    )}
                  </div>

                  {canEdit ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleToggle(p.jsonId, true)}
                            disabled={!!saving}
                            className={cx(
                              "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition",
                              record?.was_present === true
                                ? "bg-green-600 text-white border-green-600"
                                : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-green-400 hover:text-green-700 dark:hover:text-green-400"
                            )}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Présent
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggle(p.jsonId, false)}
                            disabled={!!saving}
                            className={cx(
                              "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition",
                              record?.was_present === false
                                ? "bg-red-600 text-white border-red-600"
                                : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-red-400 hover:text-red-700 dark:hover:text-red-400"
                            )}
                          >
                            <XCircle className="h-3 w-3" />
                            Absent
                          </button>
                        </>
                      )}
                    </div>
                  ) : record ? (
                    record.was_present ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        <CheckCircle2 className="h-3 w-3" />
                        Présent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                        <XCircle className="h-3 w-3" />
                        Absent
                      </span>
                    )
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add guest player modal */}
      {guestModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setGuestModalOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center p-3">
            <div className="w-full sm:max-w-lg rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">
                  Ajouter un joueur d'une autre équipe
                </div>
                <button type="button" onClick={() => setGuestModalOpen(false)}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:underline">
                  <X className="h-4 w-4" />Fermer
                </button>
              </div>

              {guestTeamOptions.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Aucune autre équipe dans ce club.
                </div>
              ) : (
                <>
                  <select
                    value={guestTeamId}
                    onChange={(e) => setGuestTeamId(e.target.value)}
                    className="w-full mb-3 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
                  >
                    {guestTeamOptions.map((ct) => (
                      <option key={ct.id} value={ct.id}>{ct.team_name}</option>
                    ))}
                  </select>

                  <div className="max-h-72 overflow-auto space-y-1.5">
                    {guestCandidatePlayers.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400 px-1 py-6 text-center">
                        Aucun joueur disponible dans cette équipe (ou déjà ajouté).
                      </div>
                    ) : (
                      guestCandidatePlayers.map((p) => (
                        <div
                          key={p.jsonId}
                          className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2"
                        >
                          <span className="text-sm text-gray-900 dark:text-gray-100">{p.name}</span>
                          <button
                            type="button"
                            onClick={() => handleAddGuest(
                              p.jsonId,
                              p.name,
                              guestTeamOptions.find((ct) => ct.id === guestTeamId)?.team_name || ""
                            )}
                            disabled={addingGuestId === p.jsonId}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 disabled:opacity-50 transition"
                          >
                            {addingGuestId === p.jsonId
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <Plus className="h-3 w-3" />}
                            Ajouter
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
